/*
 * Analyst reading of influence data: who dominates each area, who holds influence in several
 * areas, who has leverage through the network, and where the data is thin. Pure functions.
 */
import {
  concentrationLabel,
  hhi,
  NetworkAnalysis,
  ROLE_LABEL,
  StructuralRole,
} from '../graphAnalytics';
import type { InfluenceAreaScore } from '../types';
import { InfluenceArea, InfluenceEntity, InfluenceFilters } from './influenceModel';

/* ---------------- Areas ---------------- */

export type Concentration = ReturnType<typeof concentrationLabel>;

export interface AreaShare {
  entity: InfluenceEntity;
  score: number;
  /** 0–1 share of the area's influence weight */
  share: number;
}

export interface AreaSummary {
  area: InfluenceArea;
  scored: number;
  /** scored / entities in scope (0–1) */
  coverage: number;
  average: number;
  leader: AreaShare | null;
  /** Top five by score with their share of influence weight */
  top: AreaShare[];
  /** HHI (0–10,000) of influence weight */
  hhi: number;
  concentration: Concentration;
  lowConfidence: number;
}

/**
 * Influence compounds at the top (one actor at 90 outweighs three at 30), so concentration is
 * measured on influence weight = score², not on raw scores. Raw-score HHI calls almost every
 * area "fragmented" even when one actor clearly dominates.
 */
export const influenceWeight = (score: number) => Math.max(0, score) ** 2;

export const LOW_CONFIDENCE = 50;

export function summarizeAreas(areas: InfluenceArea[], entities: InfluenceEntity[], minScore = 0): AreaSummary[] {
  const inScope = entities.length;
  return areas.map((area) => {
    const holders = entities
      .map((entity) => ({ entity, s: entity.scoreByArea[area.id] }))
      .filter((h): h is { entity: InfluenceEntity; s: InfluenceAreaScore } => !!h.s && h.s.score >= minScore)
      .sort((a, b) => b.s.score - a.s.score || a.entity.label.localeCompare(b.entity.label));
    const weights = holders.map((h) => influenceWeight(h.s.score));
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    const shares: AreaShare[] = holders.map((h, i) => ({
      entity: h.entity,
      score: h.s.score,
      share: totalWeight ? weights[i] / totalWeight : 0,
    }));
    const value = hhi(weights);
    return {
      area,
      scored: holders.length,
      coverage: inScope ? holders.length / inScope : 0,
      average: holders.length ? Math.round(holders.reduce((a, h) => a + h.s.score, 0) / holders.length) : 0,
      leader: shares[0] || null,
      top: shares.slice(0, 5),
      hhi: value,
      concentration: concentrationLabel(value),
      lowConfidence: holders.filter((h) => h.s.confidence != null && h.s.confidence < LOW_CONFIDENCE).length,
    };
  });
}

export const CONCENTRATION_HINT: Record<Concentration, string> = {
  Dominated: 'One actor holds most of the influence in this area',
  Concentrated: 'A few actors hold most of the influence',
  Competitive: 'Influence is shared among several actors',
  Fragmented: 'Influence is spread widely — no clear leader',
};

/* ---------------- Cross-domain ---------------- */

export interface CrossDomainActor {
  entity: InfluenceEntity;
  strong: InfluenceAreaScore[];
}

/** Actors scoring at least `threshold` in `minAreas` or more areas, strongest first */
export function crossDomainInfluencers(
  entities: InfluenceEntity[],
  areaIds: Set<string>,
  threshold = 60,
  minAreas = 2,
): CrossDomainActor[] {
  return entities
    .map((entity) => ({ entity, strong: entity.scores.filter((s) => areaIds.has(s.categoryId) && s.score >= threshold) }))
    .filter((c) => c.strong.length >= minAreas)
    .sort(
      (a, b) =>
        b.strong.length - a.strong.length ||
        b.strong.reduce((x, s) => x + s.score, 0) - a.strong.reduce((x, s) => x + s.score, 0),
    );
}

/* ---------------- Leverage ---------------- */

export type Quadrant = 'power_broker' | 'hidden_influencer' | 'connector' | 'peripheral';

export const QUADRANTS: Quadrant[] = ['hidden_influencer', 'power_broker', 'peripheral', 'connector'];

export const QUADRANT_LABEL: Record<Quadrant, string> = {
  power_broker: 'Power brokers',
  hidden_influencer: 'Hidden influencers',
  connector: 'Connectors',
  peripheral: 'Peripheral',
};

export const QUADRANT_SINGULAR: Record<Quadrant, string> = {
  power_broker: 'Power broker',
  hidden_influencer: 'Hidden influencer',
  connector: 'Connector',
  peripheral: 'Peripheral',
};

export const QUADRANT_DESCRIPTION: Record<Quadrant, string> = {
  power_broker: 'Strong influence and a central network position — shape outcomes and can mobilise others',
  hidden_influencer: 'Strong influence but weakly tied in the recorded network — under-watched, or ties are missing',
  connector: 'Central in the network with modest influence of their own — routes to access',
  peripheral: 'Modest influence, few recorded ties',
};

export const LEVERAGE_MID = 50;

export function classifyQuadrant(networkPct: number, influence: number, mid = LEVERAGE_MID): Quadrant {
  const central = networkPct >= mid;
  const strong = influence >= mid;
  if (strong) return central ? 'power_broker' : 'hidden_influencer';
  return central ? 'connector' : 'peripheral';
}

export interface LeveragePoint {
  entity: InfluenceEntity;
  /** Network position: PageRank percentile 0–100 */
  x: number;
  /** Average influence score 0–100 (0 for unassessed) */
  y: number;
  quadrant: Quadrant | null;
  areas: number;
}

export function networkPercentile(analysis: NetworkAnalysis | null, id: string): number {
  const m = analysis?.metrics.get(id);
  if (!m || m.degree === 0) return 0;
  return Number.isFinite(m.pct.influence) ? m.pct.influence : 0;
}

export function leveragePoints(entities: InfluenceEntity[], analysis: NetworkAnalysis | null) {
  const points: LeveragePoint[] = [];
  const unassessed: LeveragePoint[] = [];
  const counts: Record<Quadrant, number> = { power_broker: 0, hidden_influencer: 0, connector: 0, peripheral: 0 };
  entities.forEach((entity) => {
    const x = networkPercentile(analysis, entity.id);
    if (!entity.assessed) {
      unassessed.push({ entity, x, y: 0, quadrant: null, areas: 0 });
      return;
    }
    const quadrant = classifyQuadrant(x, entity.average);
    counts[quadrant]++;
    points.push({ entity, x, y: entity.average, quadrant, areas: entity.scores.length });
  });
  return { points, unassessed, counts };
}

/* ---------------- Gaps ---------------- */

export const STALE_MONTHS = 12;
const HUB_ROLES: StructuralRole[] = ['hub', 'broker', 'gatekeeper', 'connector'];

export interface ScoreFlag {
  entity: InfluenceEntity;
  score: InfluenceAreaScore;
}

export interface InfluenceGaps {
  /** Unassessed entities that matter in the network, most central first */
  unassessedHubs: Array<{ entity: InfluenceEntity; role: StructuralRole; pct: number }>;
  unassessedTotal: number;
  lowConfidence: ScoreFlag[];
  stale: ScoreFlag[];
  /** Areas with fewer than three scored actors */
  thinAreas: InfluenceArea[];
  total: number;
}

export function isStale(updatedAt: string | null | undefined, now: Date = new Date(), months = STALE_MONTHS): boolean {
  if (!updatedAt) return false;
  const t = Date.parse(updatedAt);
  if (!Number.isFinite(t)) return false;
  const limit = new Date(now);
  limit.setMonth(limit.getMonth() - months);
  return t < limit.getTime();
}

export function findGaps(
  entities: InfluenceEntity[],
  areas: InfluenceArea[],
  analysis: NetworkAnalysis | null,
  now: Date = new Date(),
): InfluenceGaps {
  const unassessed = entities.filter((e) => !e.assessed);
  const unassessedHubs = unassessed
    .map((entity) => {
      const m = analysis?.metrics.get(entity.id);
      return { entity, role: (m?.role || 'isolate') as StructuralRole, pct: m && m.degree > 0 ? m.pct.influence : 0 };
    })
    .filter((u) => HUB_ROLES.includes(u.role) || u.pct >= 70)
    .sort((a, b) => b.pct - a.pct);
  const areaIds = new Set(areas.map((a) => a.id));
  const lowConfidence: ScoreFlag[] = [];
  const stale: ScoreFlag[] = [];
  entities.forEach((entity) =>
    entity.scores.forEach((score) => {
      if (!areaIds.has(score.categoryId)) return;
      if (score.confidence != null && score.confidence < LOW_CONFIDENCE) lowConfidence.push({ entity, score });
      if (isStale(score.updatedAt, now)) stale.push({ entity, score });
    }),
  );
  lowConfidence.sort((a, b) => b.score.score - a.score.score);
  stale.sort((a, b) => (a.score.updatedAt || '').localeCompare(b.score.updatedAt || ''));
  const scoredPerArea = new Map<string, number>();
  entities.forEach((e) => e.scores.forEach((s) => scoredPerArea.set(s.categoryId, (scoredPerArea.get(s.categoryId) || 0) + 1)));
  const thinAreas = entities.length >= 6 ? areas.filter((a) => (scoredPerArea.get(a.id) || 0) < 3) : [];
  return {
    unassessedHubs,
    unassessedTotal: unassessed.length,
    lowConfidence,
    stale,
    thinAreas,
    total: unassessed.length + lowConfidence.length + stale.length + thinAreas.length,
  };
}

/* ---------------- Network neighbours ---------------- */

/** Direct ties of an entity, most influential (PageRank) first */
export function influentialTies(analysis: NetworkAnalysis | null, id: string, limit = 5): string[] {
  if (!analysis) return [];
  const g = analysis.graph;
  const i = g.index.get(id);
  if (i === undefined) return [];
  return g.nbrs[i]
    .slice()
    .sort((a, b) => (analysis.metrics.get(g.ids[b])?.pagerank || 0) - (analysis.metrics.get(g.ids[a])?.pagerank || 0))
    .slice(0, limit)
    .map((j) => g.ids[j]);
}

/** "Top 5%" style reading of a percentile */
export function topShare(pct: number): string {
  const top = Math.max(1, 100 - Math.round(pct));
  if (top > 50) return 'Below median';
  return `Top ${top <= 5 ? 5 : top <= 10 ? 10 : top <= 25 ? 25 : 50}%`;
}

/* ---------------- AI: suggestions and context ---------------- */

export interface InsightBundle {
  areas: AreaSummary[];
  cross: CrossDomainActor[];
  gaps: InfluenceGaps;
  leverage: ReturnType<typeof leveragePoints>;
}

export function influenceSuggestions(bundle: InsightBundle, selected: InfluenceEntity | null): string[] {
  const out: string[] = [];
  const byWeight = bundle.areas.filter((a) => a.scored > 0).sort((a, b) => b.scored - a.scored || b.average - a.average);
  if (selected) {
    const area = selected.dominant?.label;
    out.push(area ? `How could ${selected.label} be engaged on ${area.toLowerCase()} decisions?` : `What influence does ${selected.label} likely hold?`);
    out.push(`Who has the most leverage over ${selected.label}?`);
  }
  const dominated = bundle.areas
    .filter((a) => a.scored > 1 && (a.concentration === 'Dominated' || a.concentration === 'Concentrated'))
    .sort((a, b) => b.hhi - a.hhi)[0];
  if (byWeight[0]) out.push(`Who could sway ${byWeight[0].area.label.toLowerCase()} decisions?`);
  out.push(dominated ? `How dependent is ${dominated.area.label.toLowerCase()} on ${dominated.leader?.entity.label || 'its leader'}?` : 'Where is influence most concentrated?');
  if (bundle.gaps.unassessedHubs.length) out.push('Which unscored actors should we assess first?');
  if (bundle.leverage.counts.hidden_influencer > 0) out.push('Who are the hidden influencers, and how do we reach them?');
  if (bundle.cross.length) out.push('Which actors carry influence across several areas?');
  const seen = new Set<string>();
  return out.filter((q) => (seen.has(q) ? false : (seen.add(q), true))).slice(0, 4);
}

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const ref = (e: InfluenceEntity) => `${clip(e.label, 48)} [${e.id}]`;

/**
 * Compact plain-text digest of the workspace analytics for the AI (≤ `limit` chars).
 * Ids are UUIDs; the server maps them to its own entity refs.
 */
export function buildInfluenceContext(input: {
  bundle: InsightBundle;
  entities: InfluenceEntity[];
  totalEntities: number;
  filters: InfluenceFilters;
  areaLabels: string[];
  lens: string;
  selected: InfluenceEntity | null;
  compare: InfluenceEntity[];
  analysis: NetworkAnalysis | null;
  limit?: number;
}): string {
  const { bundle, entities, filters, selected, analysis } = input;
  const limit = input.limit ?? 12000;
  const assessed = entities.filter((e) => e.assessed);
  const lines: string[] = [];
  lines.push(
    `Influence workspace · lens ${input.lens} · ${entities.length} of ${input.totalEntities} entities in scope · ${assessed.length} assessed · ${bundle.areas.length} areas`,
  );
  const filterBits = [
    filters.type !== 'all' ? `type ${filters.type}` : null,
    filters.areaIds ? `areas ${input.areaLabels.join(', ')}` : null,
    filters.minScore > 0 ? `min score ${filters.minScore}` : null,
    !filters.showUnassessed ? 'unassessed hidden' : null,
  ].filter(Boolean);
  if (filterBits.length) lines.push(`Filters: ${filterBits.join(' · ')}`);
  if (selected) {
    const m = analysis?.metrics.get(selected.id);
    lines.push(
      `Selected: ${ref(selected)} — ${selected.subtitle}; ${selected.scores.map((s) => `${s.label} ${s.score}`).join(', ') || 'unassessed'}${
        m ? `; network ${ROLE_LABEL[m.role]}, influence pct ${m.pct.influence}, brokerage pct ${m.pct.brokerage}` : ''
      }`,
    );
  }
  if (input.compare.length > 1) lines.push(`Comparing: ${input.compare.map(ref).join('; ')}`);

  lines.push('', '## Power by area (concentration = HHI of score², leaders with share of influence weight)');
  bundle.areas.forEach((a) => {
    lines.push(
      `- ${a.area.label}: ${a.scored} scored, avg ${a.average}, ${a.concentration} (HHI ${a.hhi})${a.lowConfidence ? `, ${a.lowConfidence} low-confidence` : ''}; leaders: ${
        a.top.map((t) => `${ref(t.entity)} ${t.score} (${Math.round(t.share * 100)}%)`).join('; ') || 'none'
      }`,
    );
  });

  lines.push('', '## Cross-domain influencers (≥60 in 2+ areas)');
  lines.push(bundle.cross.length ? bundle.cross.slice(0, 12).map((c) => `- ${ref(c.entity)}: ${c.strong.map((s) => `${s.label} ${s.score}`).join(', ')}`).join('\n') : '- none');

  lines.push('', '## Leverage (x = network PageRank percentile, y = average influence; midpoint 50)');
  (['power_broker', 'hidden_influencer', 'connector'] as Quadrant[]).forEach((q) => {
    const members = bundle.leverage.points
      .filter((p) => p.quadrant === q)
      .sort((a, b) => b.y + b.x - (a.y + a.x))
      .slice(0, 10);
    lines.push(`- ${QUADRANT_LABEL[q]} (${bundle.leverage.counts[q]}): ${members.map((p) => `${ref(p.entity)} infl ${p.y} net ${p.x}`).join('; ') || 'none'}`);
  });

  lines.push('', '## Gaps');
  lines.push(
    `- Unassessed network actors (${bundle.gaps.unassessedHubs.length} of ${bundle.gaps.unassessedTotal} unassessed): ${
      bundle.gaps.unassessedHubs.slice(0, 10).map((u) => `${ref(u.entity)} ${ROLE_LABEL[u.role]} pct ${u.pct}`).join('; ') || 'none'
    }`,
  );
  lines.push(`- Low-confidence scores (${bundle.gaps.lowConfidence.length}): ${bundle.gaps.lowConfidence.slice(0, 8).map((f) => `${ref(f.entity)} ${f.score.label} ${f.score.score} conf ${f.score.confidence}`).join('; ') || 'none'}`);
  lines.push(`- Stale scores >12 months (${bundle.gaps.stale.length}): ${bundle.gaps.stale.slice(0, 8).map((f) => `${ref(f.entity)} ${f.score.label} ${f.score.updatedAt?.slice(0, 10)}`).join('; ') || 'none'}`);
  if (bundle.gaps.thinAreas.length) lines.push(`- Thin areas (<3 scored): ${bundle.gaps.thinAreas.map((a) => a.label).join(', ')}`);

  const header = lines.join('\n');
  const ranked = assessed.slice().sort((a, b) => b.average - a.average || b.max - a.max);
  const rows: string[] = ['', '## Assessed actors by average influence'];
  let text = header;
  for (let i = 0; i < ranked.length; i++) {
    const e = ranked[i];
    const m = analysis?.metrics.get(e.id);
    const row = `- ${ref(e)} ${e.type === 'organization' ? 'org' : 'person'} · ${clip(e.subtitle, 60)} · avg ${e.average} · ${e.scores
      .map((s) => `${s.label} ${s.score}${s.confidence != null ? ` c${s.confidence}` : ''}`)
      .join(', ')}${m ? ` · net ${m.pct.influence}` : ''}`;
    if (text.length + rows.join('\n').length + row.length + 40 > limit) {
      rows.push(`- … ${ranked.length - i} more`);
      break;
    }
    rows.push(row);
  }
  text = `${header}\n${rows.join('\n')}`;
  return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
}
